import { useMemo, useState } from 'react';
import { Clock, Stethoscope, CheckCircle2, PauseCircle, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Card, CardTitle } from '@/components/ui/Card';
import { KpiTile } from '@/components/ui/KpiTile';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { DataTable, type DataTableColumn } from '@/components/ui/DataTable';
import { SkeletonRows } from '@/components/ui/Skeleton';
import { EmptyState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { checkBp, checkPulse, checkTemp, checkSpo2 } from '@/lib/vitalThresholds';
import { useTodayQueue } from './useTodayQueue';
import { useUpdateQueueStatus } from './useUpdateQueueStatus';
import { useHoldConsultation, useResumeConsultation, useStartEmergencyConsultation } from './useConsultationActions';
import { HoldConsultationDialog } from './HoldConsultationDialog';
import { queueStatusUrl } from '@/lib/publicLinks';
import { OutOfOrderDialog } from './OutOfOrderDialog';
import type { QueueItem, Priority, ConsultationConflictDetails } from './types';
import { withTitle } from '@/lib/patientName';

/**
 * First screen migrated off Create React App onto the new stack — see
 * HMS_UI_REDESIGN_ANALYSIS.md and HMS_V4_API_CONTRACT.md. Drop-in replacement
 * for src/components/QueueView.jsx: same two props, same behaviour, wired
 * into App.jsx by swapping one import. Every other screen is unchanged and
 * still legacy .jsx.
 *
 * Deliberately a visual reproduction, not a redesign — see Button.tsx's
 * reasoning. The Design System phase (index.css retirement, preflight,
 * visual refresh) is a separate, later step taken once every screen sits on
 * these primitives.
 */

interface QueueViewProps {
  onStartConsult: (item: QueueItem) => void;
  onOpenVisit: (visitCode: string) => void;
}

// Bespoke inline styling in the original component (fontWeight 700, padding
// '2px 8px', borderRadius 10) — deliberately NOT the same shape as the
// generic Badge primitive's four status variants, and Urgent's ink (#92400E)
// is a distinct amber from Badge's `warn` variant (#B45309). Reproduced
// exactly rather than folded into Badge, which would either need a
// special-cased fifth variant or silently shift this colour.
const PRIORITY_STYLE: Record<Exclude<Priority, 'Normal'>, { bg: string; color: string }> = {
  Emergency: { bg: '#FEE2E2', color: '#991B1B' },
  Urgent: { bg: '#FEF3C7', color: '#92400E' },
};

// Assessment backlog #18 (Queue density pass): priority used to be its own
// column, low-contrast against the row (a small pill easy to miss while
// scanning down a long list). Promoted to a left border stripe on the row
// itself instead — reads at a glance without decoding a cell — with the
// PriorityBadge kept inline next to the patient's name so the same
// information still exists in text/colour, not stripe-colour alone.
const PRIORITY_STRIPE: Record<Priority, string> = {
  Emergency: 'border-l-4 border-l-danger',
  Urgent: 'border-l-4 border-l-warn',
  Normal: 'border-l-4 border-l-transparent',
};

// Beyond this many minutes with no doctor picked, flag it the same way an
// overrunning consult gets flagged — a reception/triage staffing gap, not
// just a stage in the pipeline. No configurable setting exists for this yet
// (unlike queue_settings' delay_notify_threshold_minutes, which is scoped to
// booked-slot delay notifications specifically); a fixed default is enough
// for a visual flag.
const UNASSIGNED_FLAG_MINUTES = 20;

function PriorityBadge({ priority }: { priority: Priority }) {
  if (priority === 'Normal') return null;
  const s = PRIORITY_STYLE[priority];
  return (
    <span
      className="rounded-[10px] px-2 py-0.5 text-[11px] font-bold"
      style={{ background: s.bg, color: s.color }}
    >
      {priority}
    </span>
  );
}

// Why a routine patient sits where they do, from the server's Queue
// Prioritization engine (backend/utils/queuePriority.js). Shown only when it
// explains a move — a plain walk-in or a booked patient needs no badge.
// Every routine patient shows their category — staff asked to see Walk-in and
// Appointment too, not only the preferences that move someone up. Emergency /
// Urgent already have PriorityBadge. The waiting-time safeguard is an extra
// badge beside the category, never a replacement for it.
const CATEGORY_BADGE: Record<string, { label: string; variant: 'neutral' | 'info' | 'success'; title: string }> = {
  walk_in: { label: 'Walk-in', variant: 'neutral', title: 'Routine walk-in — ordered by check-in time' },
  appointment: { label: 'Appointment', variant: 'info', title: 'Booked appointment — ordered by slot time when on time' },
  referral: { label: 'Referral', variant: 'success', title: 'Routine referral preference' },
  travel: { label: 'Long distance', variant: 'success', title: 'Long-distance travel preference' },
};

function PlacementBadge({ item }: { item: QueueItem }) {
  if (item.status === 'Completed' || !item.queue_category) return null;
  if (item.queue_category === 'emergency' || item.queue_category === 'urgent') return null;
  const custom = item.queue_category.startsWith('c_');
  const meta = custom
    ? { label: item.queue_category_label ?? 'Priority', variant: 'success' as const, title: 'Custom queue priority (Admin → Queue Prioritization)' }
    : CATEGORY_BADGE[item.queue_category];
  return (
    <>
      {meta && <Badge variant={meta.variant} title={meta.title}>{meta.label}</Badge>}
      {item.queue_safeguard && item.status === 'Waiting' && (
        <Badge variant="warn" title="Waited past this category's waiting-time limit, so moved ahead of routine patients who have not">
          ⏱ Waited {item.queue_wait_minutes} min
        </Badge>
      )}
    </>
  );
}

// Assessment backlog #18: "vitals preview is a mono string" — one long
// `BP 120/80 · PR 88 · T 98.6°F` blob gave every reading equal weight and no
// way to spot an out-of-range value without reading the whole thing. Small
// per-vital chips instead, each checked against the same advisory ranges
// `VitalsStage`/`PreConsultStage` already use (`lib/vitalThresholds.ts`) so
// an abnormal reading tints amber here exactly as it would on those
// screens — not a second, disagreeing set of cut-offs for this table.
function VitalChip({ label, value, warn }: { label: string; value: string; warn: boolean }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[10.5px] leading-none',
        warn ? 'bg-warn-soft text-warn-ink font-bold' : 'bg-muted text-ink-muted'
      )}
    >
      {label} {value}
    </span>
  );
}

const STATUS_BADGE_VARIANT: Record<QueueItem['status'], 'warn' | 'info' | 'success'> = {
  Waiting: 'warn',
  'In Consultation': 'info',
  // Deliberately the same amber as Waiting rather than a fifth colour: to a
  // reader scanning the column, "On Hold" means the same thing operationally
  // (the doctor is not with them right now), and Badge's four status variants
  // are a fixed set — see Badge.tsx.
  'On Hold': 'warn',
  Completed: 'success',
};

// "Ready for Consultation" is what 'Waiting' means once a doctor is assigned,
// and §7 asks for the two to be unmistakably distinct on screen. The stored
// value stays 'Waiting' (every existing query, report and public status page
// keys off it) — only the label staff read changes.
/** Under a held row's Resume: what it is waiting for, and the visit's lab results. */
function HoldInfo({ item }: { item: QueueItem }) {
  const allReady = item.lab_total > 0 && item.lab_ready >= item.lab_total;
  return (
    <div className="flex flex-wrap items-center gap-1 text-[11px] text-ink-muted">
      {item.hold_reason && <span>⏸ {item.hold_reason}</span>}
      {item.lab_total > 0 && (
        <Badge variant={allReady ? 'success' : 'neutral'}
          title={allReady ? 'All lab results are ready — resume when convenient' : 'Lab results still pending'}>
          🧪 {allReady ? 'Results ready' : `${item.lab_ready}/${item.lab_total} ready`}
        </Badge>
      )}
    </div>
  );
}

function statusLabel(item: QueueItem): string {
  if (item.status === 'Waiting') return item.doctor_id ? 'Ready for Consultation' : 'Waiting';
  if (item.status === 'On Hold') return 'Consultation On Hold';
  return item.status;
}

const STAT_TILE_META: Record<QueueItem['status'], { accentClassName: string; iconClassName: string; icon: typeof Clock }> = {
  Waiting: { accentClassName: 'border-l-[3px] border-l-warn', iconClassName: 'bg-warn-soft text-warn-ink', icon: Clock },
  'In Consultation': { accentClassName: 'border-l-[3px] border-l-teal', iconClassName: 'bg-info-soft text-info-ink', icon: Stethoscope },
  'On Hold': { accentClassName: 'border-l-[3px] border-l-warn', iconClassName: 'bg-warn-soft text-warn-ink', icon: PauseCircle },
  Completed: { accentClassName: 'border-l-[3px] border-l-success', iconClassName: 'bg-success-soft text-success-ink', icon: CheckCircle2 },
};

const STAT_TILES: Array<{ status: QueueItem['status'] }> = [
  { status: 'Waiting' },
  { status: 'In Consultation' },
  { status: 'On Hold' },
  { status: 'Completed' },
];

/**
 * Pulls the structured consultation-state refusal off an ApiError. The
 * backend always answers these with 409 + `details.reason` (never a bare
 * 500 — that regression is exactly what §11 calls out), so the screen can
 * say who is busy and offer the right next action instead of printing a
 * stack-trace-shaped message.
 */
function consultationConflict(err: unknown): ConsultationConflictDetails | null {
  if (!(err instanceof ApiError) || err.status !== 409 || !err.details) return null;
  const details = err.details as unknown as ConsultationConflictDetails;
  return details.reason ? details : null;
}

export default function QueueView({ onStartConsult, onOpenVisit }: QueueViewProps) {
  const { data: queue, isLoading, isError, error, refetch } = useTodayQueue();
  const updateStatus = useUpdateQueueStatus();
  const startEmergency = useStartEmergencyConsultation();
  const resume = useResumeConsultation();
  const hold = useHoldConsultation();
  // Hold a consultation (waiting for lab results etc.). `then` is the patient
  // to start straight after — set from the Doctor busy card's one-click
  // "Hold X & see Y".
  const [holdFor, setHoldFor] = useState<{ id: number; name: string; then?: QueueItem } | null>(null);

  // The doctor-busy refusal, held against the patient it was raised for, so
  // the banner names both patients and the row's own action can turn into
  // "Hold & Attend Emergency" where that applies. Cleared on the next action.
  const [blocked, setBlocked] = useState<{ item: QueueItem; details: ConsultationConflictDetails; message: string } | null>(null);
  // Emergency interruption is destructive-ish to someone else's consultation,
  // so it asks first (§11).
  const [confirmEmergency, setConfirmEmergency] = useState<QueueItem | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Queue Prioritization call order: the server refused because this patient
  // is not next. Asks for a reason, then retries with it.
  const [outOfOrder, setOutOfOrder] = useState<{ item: QueueItem; details: ConsultationConflictDetails } | null>(null);

  const counts = useMemo(() => {
    const c: Record<QueueItem['status'], number> = { Waiting: 0, 'In Consultation': 0, 'On Hold': 0, Completed: 0 };
    queue?.forEach((q) => { c[q.status]++; });
    return c;
  }, [queue]);

  // Grouped by doctor — real assignment (queue.doctor_id), not the cosmetic
  // stage_data it used to read from. A patient with no doctor picked yet
  // (pre-Queue-stage, or an older visit) lands in its own "Unassigned" group
  // rather than being silently dropped — see ENHANCED_QUEUE_ASSIGNMENT_DESIGN.md.
  const groups = useMemo(() => {
    const g: Record<string, QueueItem[]> = {};
    queue?.forEach((item) => {
      const key = item.doctor_name || 'Unassigned';
      (g[key] ??= []).push(item);
    });
    return g;
  }, [queue]);

  const groupNames = Object.keys(groups).sort((a, b) =>
    a === 'Unassigned' ? 1 : b === 'Unassigned' ? -1 : a.localeCompare(b)
  );

  // The doctor may already be with someone else. The backend decides that
  // (utils/consultationState.js) and refuses with 409 `doctor_busy`; the
  // optimistic patch in useUpdateQueueStatus rolls itself back, so the row
  // visibly stays Ready for Consultation — which is exactly the rule. The
  // consultation screen is only opened once the server has actually granted
  // it, never before.
  const startConsult = (item: QueueItem, outOfOrderReason?: string) => {
    setBlocked(null); setActionError(null);
    updateStatus.mutate(
      { id: item.id, status: 'In Consultation', outOfOrderReason },
      {
        onSuccess: () => { setOutOfOrder(null); onStartConsult({ ...item, status: 'In Consultation' }); },
        onError: (err) => {
          const details = consultationConflict(err);
          setOutOfOrder(null);
          if (details?.reason === 'out_of_order') setOutOfOrder({ item, details });
          else if (details) setBlocked({ item, details, message: (err as Error).message });
          else setActionError(err instanceof Error ? err.message : 'Could not start this consultation');
        },
      }
    );
  };

  // §3/§8: hold the doctor's current patient and start this emergency one, in
  // one server transaction. Not a second "start consult" with the check
  // skipped — the interruption is the action.
  const attendEmergency = (item: QueueItem) => {
    setConfirmEmergency(null); setBlocked(null); setActionError(null);
    startEmergency.mutate(item.id, {
      onSuccess: () => onStartConsult({ ...item, status: 'In Consultation' }),
      onError: (err) => setActionError(err instanceof Error ? err.message : 'Could not start the emergency consultation'),
    });
  };

  const confirmHold = (reason: string) => {
    if (!holdFor) return;
    const { id, then } = holdFor;
    setActionError(null);
    hold.mutate({ id, reason }, {
      onSuccess: () => {
        setHoldFor(null);
        setBlocked(null);
        if (then) startConsult(then);
      },
      onError: (err) => { setHoldFor(null); setActionError(err instanceof Error ? err.message : 'Could not put the consultation on hold'); },
    });
  };

  const resumeConsult = (item: QueueItem) => {
    setBlocked(null); setActionError(null);
    resume.mutate(item.id, {
      onSuccess: () => onStartConsult({ ...item, status: 'In Consultation' }),
      onError: (err) => {
        const details = consultationConflict(err);
        if (details) setBlocked({ item, details, message: (err as Error).message });
        else setActionError(err instanceof Error ? err.message : 'Could not resume this consultation');
      },
    });
  };

  // Opens the patient's actual visit live, wherever its stepper currently
  // sits (Vitals, Pre-Consult, Queue, Consultation, ...) — unlike
  // startConsult above, this never mutates queue status, since a queue row
  // with no doctor yet (still working through earlier stages) isn't
  // actually "in consultation" just because staff clicked through to look.
  const openWorkflow = (item: QueueItem) => {
    if (item.visit_code) onOpenVisit(item.visit_code);
  };

  const columns: DataTableColumn<QueueItem>[] = [
    {
      key: 'token',
      header: 'Token',
      sortable: true,
      sortValue: (item) => (item.display_token != null ? Number(item.display_token) : null),
      render: (item) => <b className="text-[15px] text-teal">{item.display_token ? `#${item.display_token}` : '—'}</b>,
    },
    {
      key: 'visit_code',
      header: 'Visit ID',
      render: (item) => <span className="font-mono text-[11.5px] text-ink-muted">{item.visit_code || '—'}</span>,
    },
    {
      key: 'patient',
      header: 'Patient',
      sortable: true,
      sortValue: (item) => item.name,
      // Priority moved here from its own column (see PRIORITY_STRIPE above) —
      // the row's left-border stripe carries it for a fast visual scan, and
      // this badge keeps the same fact in text/colour beside the name it
      // describes, so nothing here depends on colour alone.
      render: (item) => (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="font-semibold">{withTitle(item.name, item.patient_title)}</span>
            <PriorityBadge priority={item.priority} />
            <PlacementBadge item={item} />
          </div>
          <div className="text-[11px] text-ink-muted">
            {item.gender}, {item.age}y · {item.uhid}
          </div>
        </>
      ),
    },
    {
      key: 'eta',
      header: 'Timing',
      // Assessment backlog #18: this cell used to switch between three
      // unrelated meanings (waiting ETA, elapsed consult, unassigned wait)
      // with nothing but a nested ternary telling them apart, so a reader had
      // to infer which one they were looking at from the wording alone. A
      // small uppercase caption now states which meaning is showing before
      // the value, rather than splitting into three mostly-empty columns —
      // a row only ever has one of the three, so a second/third column would
      // be blank for every row except the one case it applies to.
      render: (item) => {
        if (item.status === 'Waiting' && item.eta_minutes != null) {
          return (
            <div className="text-xs text-ink-muted">
              <div className="text-[9.5px] uppercase tracking-wide text-ink-muted/70">Waiting</div>
              ~{item.eta_minutes}{item.consult_overrunning ? '+' : ''} min
            </div>
          );
        }
        if (item.status === 'In Consultation' && item.consult_elapsed_minutes != null) {
          return (
            <div className="text-xs text-ink-muted">
              <div className="text-[9.5px] uppercase tracking-wide text-ink-muted/70">In consult</div>
              <span className={item.consult_overrunning ? 'font-bold text-warn-ink' : ''}>
                {item.consult_elapsed_minutes} min{item.consult_overrunning ? ' ⚠' : ''}
              </span>
            </div>
          );
        }
        if (item.status === 'On Hold' && item.held_at) {
          return (
            <div className="text-xs text-ink-muted">
              <div className="text-[9.5px] uppercase tracking-wide text-ink-muted/70">On hold</div>
              <span className="font-bold text-warn-ink">
                {Math.round((Date.now() - new Date(item.held_at).getTime()) / 60000)} min
              </span>
              {item.hold_reason && <div className="text-[10px]">{item.hold_reason}</div>}
            </div>
          );
        }
        if (item.status === 'Waiting' && item.unassigned_elapsed_minutes != null) {
          const flagged = item.unassigned_elapsed_minutes >= UNASSIGNED_FLAG_MINUTES;
          return (
            <div className="text-xs text-ink-muted">
              <div className="text-[9.5px] uppercase tracking-wide text-ink-muted/70">Unassigned</div>
              <span className={flagged ? 'font-bold text-danger' : ''}>
                {item.unassigned_elapsed_minutes} min{flagged ? ' ⚠' : ''}
              </span>
            </div>
          );
        }
        return <span className="text-xs text-ink-muted">—</span>;
      },
    },
    {
      key: 'vitals',
      header: 'Vitals preview',
      render: (item) =>
        item.bp_systolic == null && item.pulse_rate == null && item.temperature == null && item.spo2 == null ? (
          <span className="text-xs text-ink-muted">Not recorded</span>
        ) : (
          <div className="flex flex-wrap gap-1">
            {item.bp_systolic != null && (
              <VitalChip label="BP" value={`${item.bp_systolic}/${item.bp_diastolic}`} warn={checkBp(item.bp_systolic, item.bp_diastolic).warn} />
            )}
            {item.pulse_rate != null && <VitalChip label="PR" value={String(item.pulse_rate)} warn={checkPulse(item.pulse_rate).warn} />}
            {item.temperature != null && <VitalChip label="T" value={`${item.temperature}°F`} warn={checkTemp(item.temperature).warn} />}
            {item.spo2 != null && <VitalChip label="SpO₂" value={`${item.spo2}%`} warn={checkSpo2(item.spo2).warn} />}
          </div>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortValue: (item) => item.status,
      render: (item) => <Badge variant={STATUS_BADGE_VARIANT[item.status]}>{statusLabel(item)}</Badge>,
    },
    {
      key: 'action',
      header: 'Action',
      render: (item) => {
        const isUnassigned = !item.doctor_name;
        const clickable = !!item.visit_code;
        return (
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            {isUnassigned ? (
              // No doctor assigned yet means this visit hasn't reached the
              // Queue stage's own doctor-pick step — it's still sitting at
              // whatever earlier stage it's actually at. Open the workflow at
              // its real current stage instead, with no status side-effect.
              clickable && (
                <Button variant="ghost" size="sm" onClick={() => openWorkflow(item)}>
                  🔗 Open workflow
                  {item.visit_current_stage ? ` (${item.visit_current_stage.replace('_', ' ')})` : ''} →
                </Button>
              )
            ) : item.status === 'On Hold' ? (
              // §5: the doctor returns to the same consultation, same
              // context. Nothing was lost while it was paused. A parked hold
              // (waiting for lab results etc.) says what it is waiting for and
              // whether the visit's lab results are in.
              <div className="flex flex-col items-start gap-1">
                <Button
                  size="sm"
                  variant={item.queue_parked && item.lab_total > 0 && item.lab_ready < item.lab_total ? 'ghost' : 'primary'}
                  loading={resume.isPending && resume.variables === item.id}
                  onClick={() => resumeConsult(item)}
                >
                  ▶ Resume consultation
                </Button>
                <HoldInfo item={item} />
              </div>
            ) : (
              item.status !== 'Completed' && (
                <>
                  <Button
                    size="sm"
                    loading={updateStatus.isPending && updateStatus.variables?.id === item.id}
                    onClick={() => startConsult(item)}
                  >
                    {item.status === 'Waiting' ? '🩺 Start consult' : 'Continue →'}
                  </Button>
                  {/* Pause this patient (waiting for lab results, a scan…)
                      so the doctor can see the next one. */}
                  {item.status === 'In Consultation' && (
                    <Button size="sm" variant="ghost" title="Put on hold while waiting for results"
                      onClick={() => setHoldFor({ id: item.id, name: item.name })}>
                      ⏸ Hold
                    </Button>
                  )}
                  {/* Only offered once the server has actually said this
                      patient is blocked by a normal consultation it would
                      allow interrupting — never speculatively, so an
                      emergency never becomes a one-click way around the
                      availability rule. */}
                  {blocked?.item.id === item.id && blocked.details.can_hold_for_emergency && (
                    <Button
                      size="sm"
                      variant="danger"
                      loading={startEmergency.isPending && startEmergency.variables === item.id}
                      onClick={() => setConfirmEmergency(item)}
                    >
                      Hold &amp; attend emergency
                    </Button>
                  )}
                </>
              )
            )}
            {item.status_token && (
              <a
                href={queueStatusUrl(item.status_token)}
                target="_blank"
                rel="noopener noreferrer"
                className="whitespace-nowrap text-[11px] font-semibold text-teal"
              >
                🔗 Status page
              </a>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="fade-in">
      <div className="grid4 mb-5">
        {STAT_TILES.map(({ status }) => {
          const meta = STAT_TILE_META[status];
          const Icon = meta.icon;
          return (
            <KpiTile
              key={status}
              icon={<Icon className="h-4 w-4" />} iconClassName={meta.iconClassName} accentClassName={meta.accentClassName}
              value={counts[status]} label={status === 'On Hold' ? 'On hold' : status}
            />
          );
        })}
      </div>

      {/* §11: "Doctor Busy", naming both patients and stating plainly where
          the blocked patient stayed — never a generic 500. */}
      {blocked && (
        <Card className="mb-4 border-l-4 border-l-warn">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 rounded-md bg-warn-soft p-1.5 text-warn-ink"><AlertTriangle className="h-4 w-4" /></span>
            <div className="flex-1">
              <div className="text-sm font-bold">Doctor busy</div>
              <div className="mt-1 text-[13px] text-ink-muted">{blocked.message}</div>
              <div className="mt-1 text-[13px] text-ink-muted">
                <b>{blocked.item.name}</b> has not been moved — they remain{' '}
                <b>{blocked.details.patient_status === 'On Hold' ? 'Consultation On Hold' : 'Ready for Consultation'}</b>.
              </div>
              {blocked.details.can_hold_for_emergency && (
                <div className="mt-1 text-[13px] text-ink-muted">
                  This is an emergency patient — use <b>Hold &amp; attend emergency</b> on their row to interrupt the
                  current consultation.
                </div>
              )}
              {/* The everyday way past "doctor busy": the current patient is
                  waiting for something (lab results…), so park them and see
                  this one. Not offered for an emergency consultation. */}
              {blocked.details.reason === 'doctor_busy' && blocked.details.active_consultation
                && blocked.details.active_consultation.priority !== 'Emergency' && (
                <Button size="sm" className="mt-2"
                  onClick={() => setHoldFor({
                    id: blocked.details.active_consultation!.queue_id,
                    name: blocked.details.active_consultation!.patient_name,
                    then: blocked.item,
                  })}>
                  ⏸ Hold {blocked.details.active_consultation.patient_name} &amp; see {blocked.item.name}
                </Button>
              )}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setBlocked(null)}>Dismiss</Button>
          </div>
        </Card>
      )}

      {actionError && (
        <Card className="mb-4 border-l-4 border-l-danger">
          <div className="flex items-start gap-3">
            <div className="flex-1 text-[13px] text-danger">{actionError}</div>
            <Button variant="ghost" size="sm" onClick={() => setActionError(null)}>Dismiss</Button>
          </div>
        </Card>
      )}

      {confirmEmergency && (
        <Card className="mb-4 border-l-4 border-l-danger">
          <div className="text-sm font-bold">Hold the current consultation?</div>
          <div className="mt-1 text-[13px] text-ink-muted">
            The current consultation will be temporarily placed on hold. You can resume it after completing the
            emergency consultation — nothing already recorded is lost.
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              variant="danger"
              size="sm"
              loading={startEmergency.isPending}
              onClick={() => attendEmergency(confirmEmergency)}
            >
              Hold &amp; attend {confirmEmergency.name}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setConfirmEmergency(null)}>Cancel</Button>
          </div>
        </Card>
      )}

      {isLoading ? (
        <SkeletonRows rows={5} columns={7} />
      ) : isError ? (
        <ErrorState
          message={error instanceof ApiError ? error.message : 'Failed to load queue'}
          onRetry={() => refetch()}
        />
      ) : !queue?.length ? (
        <EmptyState label="No patients in queue today" />
      ) : (
        groupNames.map((doctorName) => (
          <Card key={doctorName} className="mb-4">
            <CardTitle className="mb-3.5">
              {doctorName === 'Unassigned' ? '❔' : '👨‍⚕️'} {doctorName}
              <span className="text-[11px] font-medium text-ink-muted">
                ({groups[doctorName]!.filter((q) => q.status === 'Waiting').length} waiting)
              </span>
            </CardTitle>
            <DataTable
              columns={columns}
              rows={groups[doctorName]!}
              rowKey={(item) => item.id}
              onRowClick={(item) => item.visit_code && openWorkflow(item)}
              rowClassName={(item) => PRIORITY_STRIPE[item.priority]}
              className="max-h-[70vh]"
            />
          </Card>
        ))
      )}

      <HoldConsultationDialog
        open={!!holdFor}
        patientName={holdFor?.name ?? 'this patient'}
        nextPatientName={holdFor?.then?.name}
        pending={hold.isPending}
        onCancel={() => setHoldFor(null)}
        onConfirm={confirmHold}
      />

      <OutOfOrderDialog
        open={!!outOfOrder}
        patientName={outOfOrder?.item.name ?? 'this patient'}
        expected={outOfOrder?.details.expected_next ?? null}
        position={outOfOrder?.details.position}
        pending={updateStatus.isPending}
        onCancel={() => setOutOfOrder(null)}
        onConfirm={(reason) => outOfOrder && startConsult(outOfOrder.item, reason)}
      />
    </div>
  );
}
